import React from "react";
import {
  Users,
  Award,
  Clock,
  ShieldCheck,
  MapPin,
  Mail,
  Phone,
  Instagram,
  Facebook,
  Twitter,
  Truck,
  DollarSign,
  Package,
  HeartHandshake,
} from "lucide-react";
import { Link } from "react-router-dom";

const About = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <header className="bg-gradient-to-r from-orange-500 to-orange-600 text-white">
        <div className="container mx-auto px-4 py-20 text-center">
          <h1 className="text-4xl md:text-5xl font-bold mb-4">
            About BricknBar
          </h1>
          <p className="text-xl max-w-2xl mx-auto">
            Revolutionizing the way construction professionals and homeowners
            source building materials
          </p>
        </div>
      </header>

      {/* Our Story Section */}
      <section className="py-8 px-4 sm:px-6 lg:px-8">
        <div className="container mx-auto w-full">
          <h2 className="text-3xl font-bold text-center mb-10">Who We Are</h2>
          <div className="flex flex-col md:flex-row gap-8 items-center">
            <div className="w-full">
              <p className="text-gray-700 mb-4">
                At <span className="font-bold">BricknBar</span>, we are
                revolutionizing the way construction professionals and
                homeowners source building materials. Born from the idea that
                quality, convenience, and transparency should be at the core of
                every construction project, we bring a wide range of high-grade
                building materials right to your doorstep—faster, smarter, and
                more affordably.
              </p>
              <p className="text-gray-700 mb-4">
                We are a team of engineers, supply chain experts, and tech
                enthusiasts passionate about solving the inefficiencies in the
                construction materials industry. Whether you're building your
                dream home or managing large-scale infrastructure projects, our
                goal is to simplify your procurement process with reliable
                products, verified vendors, and seamless logistics.
              </p>
              <p className="text-gray-700">
                We're not just another supplier—we're your{" "}
                <span className="font-bold">construction partner</span>. Join
                thousands of builders and contractors who trust us to build
                better, every day.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* What We Offer */}
      <section className="bg-white py-8 px-4 sm:px-6 lg:px-8">
        <div className="container mx-auto w-full">
          <h2 className="text-3xl font-bold text-center mb-10">
            What We Offer
          </h2>

          <h3 className="text-xl font-bold text-orange-700 mb-6">
            Building Materials at Your Fingertips
          </h3>

          <div className="grid md:grid-cols-2 gap-8 mb-12">
            <div className="flex gap-4">
              <div className="shrink-0 mt-1">
                <Award className="text-orange-600" size={24} />
              </div>
              <div>
                <h4 className="font-bold text-lg mb-2">Premium Materials</h4>
                <p className="text-gray-600">
                  Cement, steel, bricks, and other essential construction
                  materials sourced from verified suppliers.
                </p>
              </div>
            </div>
            <div className="flex gap-4">
              <div className="shrink-0 mt-1">
                <ShieldCheck className="text-orange-600" size={24} />
              </div>
              <div>
                <h4 className="font-bold text-lg mb-2">
                  Electrical & Plumbing
                </h4>
                <p className="text-gray-600">
                  Comprehensive range of electrical, plumbing, and sanitary
                  fittings for all your project needs.
                </p>
              </div>
            </div>
            <div className="flex gap-4">
              <div className="shrink-0 mt-1">
                <Package className="text-orange-600" size={24} />
              </div>
              <div>
                <h4 className="font-bold text-lg mb-2">
                  Interior & Decorative
                </h4>
                <p className="text-gray-600">
                  Paints, tiles, wood, hardware, interior decorative items &
                  specialized tools.
                </p>
              </div>
            </div>
            <div className="flex gap-4">
              <div className="shrink-0 mt-1">
                <ShieldCheck className="text-orange-600" size={24} />
              </div>
              <div>
                <h4 className="font-bold text-lg mb-2">Verified Brands</h4>
                <p className="text-gray-600">
                  100% verified brands and suppliers, ensuring quality and
                  reliability for every purchase.
                </p>
              </div>
            </div>
          </div>

          <h3 className="text-xl font-bold text-orange-700 mb-6 mt-10">
            Value-Added Solutions
          </h3>

          <div className="grid md:grid-cols-2 gap-8">
            <div className="flex gap-4">
              <div className="shrink-0 mt-1">
                <Truck className="text-orange-600" size={24} />
              </div>
              <div>
                <h4 className="font-bold text-lg mb-2">Doorstep Delivery</h4>
                <p className="text-gray-600">
                  Convenient doorstep delivery service with live order tracking
                  capabilities.
                </p>
              </div>
            </div>
            <div className="flex gap-4">
              <div className="shrink-0 mt-1">
                <DollarSign className="text-orange-600" size={24} />
              </div>
              <div>
                <h4 className="font-bold text-lg mb-2">Transparent Pricing</h4>
                <p className="text-gray-600">
                  Transparent pricing with no hidden costs or middlemen markup.
                </p>
              </div>
            </div>
            <div className="flex gap-4">
              <div className="shrink-0 mt-1">
                <Package className="text-orange-600" size={24} />
              </div>
              <div>
                <h4 className="font-bold text-lg mb-2">Bulk Order Discounts</h4>
                <p className="text-gray-600">
                  Special discounts for contractors and developers on bulk
                  orders.
                </p>
              </div>
            </div>
            <div className="flex gap-4">
              <div className="shrink-0 mt-1">
                <HeartHandshake className="text-orange-600" size={24} />
              </div>
              <div>
                <h4 className="font-bold text-lg mb-2">Project Support</h4>
                <p className="text-gray-600">
                  Comprehensive project support and material planning
                  assistance.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="bg-orange-50 py-8 px-4 sm:px-6 lg:px-8">
        <div className="container mx-auto w-full">
          <h2 className="text-3xl font-bold text-center mb-10">
            Why Choose Us?
          </h2>
          <div className="grid md:grid-cols-2 gap-8">
            <div className="flex gap-4">
              <div className="shrink-0 mt-1">
                <Award className="text-orange-600" size={24} />
              </div>
              <div>
                <h3 className="font-bold text-lg mb-2">Wide Selection</h3>
                <p className="text-gray-600">
                  One-stop shop for all construction needs
                </p>
              </div>
            </div>
            <div className="flex gap-4">
              <div className="shrink-0 mt-1">
                <ShieldCheck className="text-orange-600" size={24} />
              </div>
              <div>
                <h3 className="font-bold text-lg mb-2">Assured Quality</h3>
                <p className="text-gray-600">
                  Only vetted suppliers and brands
                </p>
              </div>
            </div>
            <div className="flex gap-4">
              <div className="shrink-0 mt-1">
                <Clock className="text-orange-600" size={24} />
              </div>
              <div>
                <h3 className="font-bold text-lg mb-2">On-Time Delivery</h3>
                <p className="text-gray-600">
                  Reliable logistics to meet your project timelines
                </p>
              </div>
            </div>
            <div className="flex gap-4">
              <div className="shrink-0 mt-1">
                <Users className="text-orange-600" size={24} />
              </div>
              <div>
                <h3 className="font-bold text-lg mb-2">Customer Support</h3>
                <p className="text-gray-600">
                  Expert guidance and post-sale service
                </p>
              </div>
            </div>

            <div className="flex gap-4">
              <div className="shrink-0 mt-1">
                <Package className="text-orange-600" size={24} />
              </div>
              <div>
                <h3 className="font-bold text-lg mb-2">
                  Everything in One Place
                </h3>
                <p className="text-gray-600">
                  Materials, manpower, under one roof
                </p>
              </div>
            </div>
            <div className="flex gap-4">
              <div className="shrink-0 mt-1">
                <Truck className="text-orange-600" size={24} />
              </div>
              <div>
                <h3 className="font-bold text-lg mb-2">
                  Local Reach, Quick Delivery
                </h3>
                <p className="text-gray-600">
                  Fast logistics with best Service
                </p>
              </div>
            </div>
            <div className="flex gap-4">
              <div className="shrink-0 mt-1">
                <HeartHandshake className="text-orange-600" size={24} />
              </div>
              <div>
                <h3 className="font-bold text-lg mb-2">
                  Verified Professionals
                </h3>
                <p className="text-gray-600">
                  Skilled, background-checked service partners
                </p>
              </div>
            </div>
            <div className="flex gap-4">
              <div className="shrink-0 mt-1">
                <Users className="text-orange-600" size={24} />
              </div>
              <div>
                <h3 className="font-bold text-lg mb-2">Customer-Centric</h3>
                <p className="text-gray-600">
                  Expert support at every step—from planning to finishing
                </p>
              </div>
            </div>
          </div>

          <div className="mt-12 text-center">
            <p className="text-xl font-medium">
              We're not just a supplier—we're your{" "}
              <span className="font-bold">
                construction partner for every stage of your project
              </span>
              .
            </p>
            <p className="text-xl font-bold mt-6 text-orange-600">
              BUILDING MADE EASY
            </p>
          </div>
        </div>
      </section>

      {/* Contact Information */}
      <section className="py-8 px-4 sm:px-6 lg:px-8">
        <div className="container mx-auto w-full">
          <h2 className="text-3xl font-bold text-center mb-10">Get In Touch</h2>
          <div className="grid md:grid-cols-3 gap-8 mb-12">
            <div className="text-center">
              <div className="mx-auto w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center mb-4">
                <MapPin className="text-orange-600" size={20} />
              </div>
              <h3 className="font-bold mb-2">Our Location</h3>
              <p className="text-gray-600">
                Kalinga Bihar, Bhubaneswar, Odisha
              </p>
            </div>
            <div className="text-center">
              <div className="mx-auto w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center mb-4">
                <Mail className="text-orange-600" size={20} />
              </div>
              <h3 className="font-bold mb-2">Email Us</h3>
              <p className="text-gray-600">bricknbar1@gmail.com</p>
            </div>
            <div className="text-center">
              <div className="mx-auto w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center mb-4">
                <Phone className="text-orange-600" size={20} />
              </div>
              <h3 className="font-bold mb-2">Call Us</h3>
              <p className="text-gray-600">+91(9777)772-782</p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
